import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { View, Text, ScrollView, TextInput, Pressable, Platform } from 'react-native';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import { getFirestore, query, collection, where, getDocs, doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { debounce, max } from 'lodash';
import { logWidgetEvent } from '../services/WidgetAuditLogger';
import { validateEventCard, validateWidget, repairWidget, wouldOverwriteValidCard, safeEventCardMerge } from '../services/WidgetWriteGuard';

// Helper to detect mobile devices
const isMobile = (typeof window !== 'undefined' && window.innerWidth < 768) || (typeof Platform !== 'undefined' && Platform.OS !== 'web' && Platform.OS !== undefined);

// Mapbox public token (web only)
mapboxgl.accessToken = 'pk.eyJ1IjoibWFrZW5vaXNlbWVkaWEiLCJhIjoiY2xvZGtmdW53MDdxYjJqcGw3ZTNlNGlxMyJ9.dGhAeNbJqE5Sb1xy6eRrgw';

const ADMIN_EMAIL_WHITELIST = [
  'trevorhawkins@dsignable.com',
  'info@mytassel.com',
];

export const useEventsWidget = ({ eventDetails, setEventDetails, isEditingEvent, pageOwnerId, pageOwner, widgetIndex, widgetId, rsvpEnabled, reminderSettings, setReminderSettings, eventId, setEventId, setHasChanges, setSuccessMessage, setErrorMessage, isPreviewMode = false }) => {
  // Mapbox refs and state for editor modal
  const modalMapContainerRef = useRef(null);
  const modalMapRef = useRef(null);
  const modalMarkerRef = useRef(null);
  const modalGeocoderRef = useRef(null);
  const [modalMapInitialized, setModalMapInitialized] = useState(false);

  // Mapbox refs for saved/view map (visible on profile)
  const viewMapContainerRef = useRef(null);
  const viewMapRef = useRef(null);
  const viewMarkerRef = useRef(null);
  
  // Stable ref for setEventDetails to avoid recreating debounced functions
  const setEventDetailsRef = useRef(setEventDetails);
  useEffect(() => {
    setEventDetailsRef.current = setEventDetails;
  }, [setEventDetails]);
  
  // Local state for inputs that need immediate response without triggering re-renders
  const [localTitle, setLocalTitle] = useState(eventDetails.title || '');
  const [localDescription, setLocalDescription] = useState(eventDetails.description || '');

  // Stable debounced functions that don't recreate on every render
  const debouncedSetTitle = useCallback(
    debounce((title) => {
      console.log('[EventsWidget] debouncedSetTitle ->', title);
      try { setEventDetailsRef.current(prev => ({ ...prev, title })); } catch (e) { console.warn('[EventsWidget] debouncedSetTitle failed', e); }
    }, 300),
    [] // Empty dependency array for stability
  );

  const debouncedSetDescription = useCallback(
    debounce((description) => {
      setEventDetailsRef.current(prev => ({ ...prev, description }));
    }, 300),
    [] // Empty dependency array for stability
  );

  // Update local state when eventDetails changes from external sources
  // Use a ref to prevent infinite loops
  const lastEventDetailsRef = useRef(eventDetails);
  useEffect(() => {
    if (lastEventDetailsRef.current.title !== eventDetails.title) {
      console.log('[EventsWidget] eventDetails.title changed ->', eventDetails.title);
      setLocalTitle(eventDetails.title || '');
      lastEventDetailsRef.current.title = eventDetails.title;
    }
    if (lastEventDetailsRef.current.description !== eventDetails.description) {
      setLocalDescription(eventDetails.description || '');
      lastEventDetailsRef.current.description = eventDetails.description;
    }
  }, [eventDetails.title, eventDetails.description]);
  // Location autocomplete
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationSearchTimeout, setLocationSearchTimeout] = useState(null);
  // Track when a map click just placed a marker so the sync effect skips the redundant flyTo
  const clickPlacedMarkerRef = useRef(false);
  // Track the last location string we explicitly selected from suggestions
  const lastSelectedLocationRef = useRef(null);

  // Dynamic reminder options from Firestore
  const [dynamicReminderOptions, setDynamicReminderOptions] = useState([
    { value: 'days_before_1', label: 'Day of + 1 day before' },
    { value: 'days_before_3', label: 'Day of + 3 days before' },
    { value: 'days_before_7', label: 'Day of + 1 week before' },
    { value: 'days_before_10', label: 'Day of + 10 days before' },
  ]);

  useEffect(() => {
    const fetchReminderOptions = async () => {
      try {
        const db = getFirestore();
        const snap = await getDoc(doc(db, 'config', 'reminderSettings'));
        if (snap.exists() && snap.data().defaultOptions?.length) {
          setDynamicReminderOptions(snap.data().defaultOptions.map(opt => ({
            value: opt.value,
            label: opt.label,
          })));
        }
      } catch (err) {
        console.warn('Using fallback reminder options:', err.message);
      }
    };
    fetchReminderOptions();
  }, []);

  const searchLocations = async (queryStr) => {
    if (!queryStr || queryStr.length < 3) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    try {
      // Use Mapbox Geocoding API (client-side safe, handles CORS)
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(queryStr)}.json?access_token=${mapboxgl.accessToken}&limit=8&autocomplete=true&types=place,locality,neighborhood,address,poi`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Geocoding error: ${response.statusText}`);
      const data = await response.json();
      const suggestions = (data.features || []).map(f => ({
        display_name: f.place_name,
        name: f.text || (f.place_name && f.place_name.split(',')[0]),
        type: (f.place_type && f.place_type[0]) || '',
        address: f.place_name,
        lat: f.center ? f.center[1] : (f.geometry && f.geometry.coordinates[1]),
        lon: f.center ? f.center[0] : (f.geometry && f.geometry.coordinates[0])
      }));

      setLocationSuggestions(suggestions);
      setShowLocationSuggestions(suggestions.length > 0);
    } catch (err) {
      console.error('Location search failed:', err);
      // Fallback suggestions
      setLocationSuggestions([
        { display_name: `${queryStr} - University`, name: `${queryStr} University`, type: 'university', address: `${queryStr} University` },
        { display_name: `${queryStr} - Convention Center`, name: `${queryStr} Convention Center`, type: 'venue', address: `${queryStr} Convention Center` },
        { display_name: `${queryStr} - Hotel`, name: `${queryStr} Hotel`, type: 'hotel', address: `${queryStr} Hotel` },
        { display_name: 'Virtual Event (Zoom)', name: 'Virtual Event (Zoom)', type: 'virtual', address: 'Virtual Event (Zoom)' }
      ]);
      setShowLocationSuggestions(true);
    }
  };

  // Memoized location handlers to prevent recreation on every render
  const handleLocationChange = useCallback((text) => {
    setEventDetailsRef.current(prev => ({ ...prev, location: text }));
    if (locationSearchTimeout) clearTimeout(locationSearchTimeout);
    const timeout = setTimeout(() => searchLocations(text), 300);
    setLocationSearchTimeout(timeout);
  }, []);

  const selectLocation = useCallback((location) => {
    console.log('[EventsWidget] selectLocation called ->', location.display_name, location.lat, location.lon);
    // Remember that this value was explicitly chosen so sync effects don't accidentally clear it
    lastSelectedLocationRef.current = location.display_name;
    // Cancel any pending location search debounce to avoid overwriting selection
    try { if (locationSearchTimeout) { clearTimeout(locationSearchTimeout); setLocationSearchTimeout(null); } } catch (e) {}
    // Update both the parent state (if available) and the ref-backed setter
    try {
      if (setEventDetails) {
        setEventDetails(prev => ({ 
          ...prev, 
          location: location.display_name, 
          locationCoords: location.lat && location.lon ? { lat: Number(location.lat), lng: Number(location.lon) } : prev.locationCoords 
        }));
      } else {
        setEventDetailsRef.current(prev => ({ 
          ...prev, 
          location: location.display_name, 
          locationCoords: location.lat && location.lon ? { lat: Number(location.lat), lng: Number(location.lon) } : prev.locationCoords 
        }));
      }
    } catch (e) {
      // fallback to ref-based update
      try { setEventDetailsRef.current(prev => ({ ...prev, location: location.display_name })); } catch (_) {}
    }

    // local UI will pick this up via the exported sentinel; don't try to directly call editor setters here

    setShowLocationSuggestions(false);
    setLocationSuggestions([]);

    try {
      if (modalMapRef.current && location.lat && location.lon) {
        if (modalMarkerRef.current) modalMarkerRef.current.remove();
        modalMarkerRef.current = new mapboxgl.Marker({ color: '#4c956c' })
          .setLngLat([Number(location.lon), Number(location.lat)])
          .addTo(modalMapRef.current);
        modalMapRef.current.flyTo({ center: [Number(location.lon), Number(location.lat)], zoom: 14 });
        try { document.activeElement && document.activeElement.blur && document.activeElement.blur(); } catch(e) {}
      }
    } catch (err) {
      console.error('Failed to update modal map from selectLocation:', err);
    }

    // Broadcast a DOM event so the editor (which owns local input state) can update immediately
    try {
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('eventsWidget:selectedLocation', { detail: { location: location.display_name, coords: location.lat && location.lon ? { lat: Number(location.lat), lng: Number(location.lon) } : null } }));
      }
    } catch (e) { /* ignore */ }
  }, []);

  // Initializes Mapbox when editing begins — 3D globe with click-to-place-pin
  useEffect(() => {
    if (!isEditingEvent || !modalMapContainerRef.current || modalMapRef.current) return;

    try {
      const map = new mapboxgl.Map({
        container: modalMapContainerRef.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: eventDetails.locationCoords ? [eventDetails.locationCoords.lng, eventDetails.locationCoords.lat] : [-98.5795, 39.8283],
        zoom: eventDetails.locationCoords ? 15 : 3,
        pitch: 45,
        bearing: -17.6,
        antialias: true
      });

      map.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Add 3D buildings layer on load
      map.on('style.load', () => {
        try {
          const layers = map.getStyle().layers;
          const labelLayerId = layers.find(l => l.type === 'symbol' && l.layout['text-field'])?.id;
          map.addLayer({
            id: '3d-buildings',
            source: 'composite',
            'source-layer': 'building',
            filter: ['==', 'extrude', 'true'],
            type: 'fill-extrusion',
            minzoom: 12,
            paint: {
              'fill-extrusion-color': '#c8dfc3',
              'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 12, 0, 12.5, ['get', 'height']],
              'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], 12, 0, 12.5, ['get', 'min_height']],
              'fill-extrusion-opacity': 0.5
            }
          }, labelLayerId);
        } catch (e) { console.warn('3D buildings layer failed:', e); }
      });

      const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        marker: false,
        placeholder: 'Search for a location...'
      });

      map.addControl(geocoder);

      geocoder.on('result', (e) => {
        const { result } = e;
        setEventDetails(prev => ({
          ...prev,
          location: result.place_name,
          locationCoords: { lat: result.geometry.coordinates[1], lng: result.geometry.coordinates[0] }
        }));

        if (modalMarkerRef.current) modalMarkerRef.current.remove();
        modalMarkerRef.current = new mapboxgl.Marker({ color: '#4c956c' })
          .setLngLat(result.geometry.coordinates)
          .addTo(map);

        map.flyTo({ center: result.geometry.coordinates, zoom: 15, pitch: 45 });
      });

      // Click-to-place-pin: place marker instantly, reverse geocode for name
      map.on('click', async (e) => {
        const { lngLat } = e;
        const coords = { lat: lngLat.lat, lng: lngLat.lng };

        // Place marker at click location (no camera movement)
        if (modalMarkerRef.current) modalMarkerRef.current.remove();
        modalMarkerRef.current = new mapboxgl.Marker({ color: '#4c956c' })
          .setLngLat([coords.lng, coords.lat])
          .addTo(map);

        // Flag so the marker sync effect doesn't double-flyTo
        clickPlacedMarkerRef.current = true;

        // Reverse geocode to get a readable name
        try {
          const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${coords.lng},${coords.lat}.json?access_token=${mapboxgl.accessToken}&limit=1`);
          const data = await res.json();
          const placeName = (data.features && data.features.length > 0) ? data.features[0].place_name : '';
          setEventDetails(prev => ({ ...prev, location: placeName || prev.location, locationCoords: coords }));
        } catch (err) {
          console.warn('Reverse geocode failed:', err);
          setEventDetails(prev => ({ ...prev, locationCoords: coords }));
        }
      });

      if (eventDetails.locationCoords) {
        const { lat, lng } = eventDetails.locationCoords;
        map.flyTo({ center: [lng, lat], zoom: 15, pitch: 45 });
        modalMarkerRef.current = new mapboxgl.Marker({ color: '#4c956c' })
          .setLngLat([lng, lat])
          .addTo(map);
      }

      modalMapRef.current = map;
      modalGeocoderRef.current = geocoder;
      setModalMapInitialized(true);

      // If suggestions are open, lower the map's container z-index and disable pointer events so the dropdown can appear above and be clickable
      if (showLocationSuggestions && modalMapRef.current && modalMapRef.current.getContainer) {
        try {
          const container = modalMapRef.current.getContainer();
          container.style.zIndex = '-1';
          container.style.pointerEvents = 'none';
          try {
            const canvas = modalMapRef.current.getCanvas && modalMapRef.current.getCanvas();
            if (canvas) canvas.style.pointerEvents = 'none';
          } catch (e) {}
        } catch (e) {}
      }
    } catch (err) {
      console.error('Failed to init inline Mapbox:', err);
    }

    return () => {
      if (modalMarkerRef.current) modalMarkerRef.current.remove();
      if (modalMapRef.current) { modalMapRef.current.remove(); modalMapRef.current = null; }
      setModalMapInitialized(false);
    };
  }, [isEditingEvent]);

  // When suggestions open/close, temporarily lower the modal map stacking and disable pointer events so the dropdown is visible and clickable
  useEffect(() => {
    try {
      if (modalMapRef.current && modalMapRef.current.getContainer) {
        const container = modalMapRef.current.getContainer();
        if (showLocationSuggestions) {
          // push the map underneath the suggestion dropdown and disable interactions
          container.style.zIndex = '-1';
          container.style.pointerEvents = 'none';
        } else {
          container.style.zIndex = '0';
          container.style.pointerEvents = 'auto';
        }
        try {
          const canvas = modalMapRef.current.getCanvas && modalMapRef.current.getCanvas();
          if (canvas) canvas.style.pointerEvents = showLocationSuggestions ? 'none' : 'auto';
        } catch (e) {}
      }
    } catch (err) {
      // ignore
    }
  }, [showLocationSuggestions]);

  // Keep modal marker in sync with latest coords (e.g. from location search autocomplete)
  // Skips when a map click already placed the marker to avoid camera jitter
  useEffect(() => {
    try {
      if (!modalMapRef.current) return;
      if (!eventDetails.locationCoords) return;

      // If marker was just placed by a map click, skip — it's already positioned
      if (clickPlacedMarkerRef.current) {
        clickPlacedMarkerRef.current = false;
        return;
      }

      const { lat, lng } = eventDetails.locationCoords;
      if (typeof lat === 'undefined' || typeof lng === 'undefined') return;
      if (modalMarkerRef.current) modalMarkerRef.current.remove();
      modalMarkerRef.current = new mapboxgl.Marker({ color: '#4c956c' })
        .setLngLat([Number(lng), Number(lat)])
        .addTo(modalMapRef.current);
      modalMapRef.current.flyTo({ center: [Number(lng), Number(lat)], zoom: 15, pitch: 45 });
    } catch (err) {
      console.error('Failed to sync modal marker with event coords:', err);
    }
  }, [eventDetails.locationCoords]);

  // Initialize a view map (displayed on profile) when there are saved coords and NOT editing
  useEffect(() => {
    // Only show view map when not currently editing
    if (isEditingEvent) return;

    const cleanup = () => {
      try {
        if (viewMarkerRef.current) { viewMarkerRef.current.remove(); viewMarkerRef.current = null; }
        if (viewMapRef.current) { viewMapRef.current.remove(); viewMapRef.current = null; }
      } catch (err) {
        console.error('Error cleaning up view map:', err);
      }
    };

    // If there are no coords, clean up any existing view map
    if (!eventDetails.locationCoords) {
      cleanup();
      return;
    }

    let cancelled = false;

    const waitForContainer = () => new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        if (cancelled) return resolve(false);
        if (viewMapContainerRef.current) return resolve(true);
        if (Date.now() - start > 2000) return resolve(false);
        requestAnimationFrame(check);
      };
      check();
    });

    (async () => {
      const available = await waitForContainer();
      if (!available || cancelled) {
        console.warn('View map container not available for initialization');
        return;
      }

      try {
        // If map already exists, update marker and flyTo
        if (viewMapRef.current) {
          const { lat, lng } = eventDetails.locationCoords;
          if (viewMarkerRef.current) viewMarkerRef.current.remove();
          viewMarkerRef.current = new mapboxgl.Marker({ color: '#4c956c' })
            .setLngLat([lng, lat])
            .addTo(viewMapRef.current);
          viewMapRef.current.flyTo({ center: [lng, lat], zoom: 12 });
          return;
        }

        // Initialize the view map
        const map = new mapboxgl.Map({
          container: viewMapContainerRef.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [eventDetails.locationCoords.lng, eventDetails.locationCoords.lat],
          zoom: 12
        });

        map.addControl(new mapboxgl.NavigationControl(), 'top-right');

        const { lat, lng } = eventDetails.locationCoords;
        viewMarkerRef.current = new mapboxgl.Marker({ color: '#4c956c' })
          .setLngLat([lng, lat])
          .addTo(map);

        viewMapRef.current = map;
      } catch (err) {
        console.error('Failed to init view Mapbox:', err);
      }
    })();

    return () => { cancelled = true; cleanup(); };
  }, [eventDetails.locationCoords, isEditingEvent]);

  // Safely serialize data for Firestore — manual recursive deep-copy.
  const cleanForFirestore = (data) => {
    if (data === undefined || data === null) return null;
    const seen = new WeakSet();
    const clean = (val, depth) => {
      if (depth > 30) return null;
      if (val === undefined) return null;
      if (val === null) return null;
      if (typeof val === 'function' || typeof val === 'symbol') return null;
      if (typeof val !== 'object') return val;
      if (typeof HTMLElement !== 'undefined' && val instanceof HTMLElement) return null;
      if (val.$$typeof) return null;
      if (typeof val.then === 'function') return null;
      if (seen.has(val)) return null;
      seen.add(val);
      if (Array.isArray(val)) {
        const result = [];
        for (let i = 0; i < val.length; i++) {
          const item = clean(val[i], depth + 1);
          if (Array.isArray(item)) {
            for (let j = 0; j < item.length; j++) {
              if (item[j] !== null && item[j] !== undefined) result.push(item[j]);
            }
          } else if (item !== null && item !== undefined) {
            result.push(item);
          }
        }
        return result;
      }
      const out = {};
      let keys;
      try { keys = Object.keys(val); } catch (_) { return null; }
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (k.length > 4 && k.startsWith('__') && k.endsWith('__')) continue;
        try {
          const v = val[k];
          if (typeof v === 'function' || typeof v === 'symbol') continue;
          if (v && v.$$typeof) continue;
          const c = clean(v, depth + 1);
          if (c !== undefined) out[k] = c;
        } catch (_) { /* skip */ }
      }
      return out;
    };
    return clean(data, 0);
  };

  const saveEventDetails = async (options = {}) => {
    const {
      targetWidgetId = null,
      targetWidgetIndex = null,
      overrideEventDetails = null,
      overrideRsvpEnabled = null,
      overrideReminderSettings = null,
      overrideEventUrl = null,
    } = options;

    if (!pageOwnerId) return;

    const effectiveEventDetails = overrideEventDetails || eventDetails || {};
    const effectiveRsvpEnabled =
      typeof overrideRsvpEnabled === 'boolean' ? overrideRsvpEnabled : !!rsvpEnabled;
    const effectiveReminderSettings =
      overrideReminderSettings || reminderSettings || { enabled: false, reminderTimes: [] };
    const effectiveEventUrl =
      typeof overrideEventUrl === 'string' ? overrideEventUrl : (effectiveEventDetails.url || '');

    // Validation: title and date are both required.
    if (!effectiveEventDetails.title || !effectiveEventDetails.title.toString().trim()) {
      setErrorMessage && setErrorMessage('Please provide an event title before saving.');
      setTimeout(() => setErrorMessage && setErrorMessage(''), 3000);
      return;
    }
    if (!effectiveEventDetails.date || !effectiveEventDetails.date.toString().trim()) {
      setErrorMessage && setErrorMessage('Please select an event date before saving.');
      setTimeout(() => setErrorMessage && setErrorMessage(''), 3000);
      return;
    }

    // Reminder validation: if reminders are enabled we must have at least one timeframe selected
    if (effectiveReminderSettings.enabled &&
        (!effectiveReminderSettings.reminderTimes || effectiveReminderSettings.reminderTimes.length === 0)) {
      setErrorMessage && setErrorMessage('Please select a reminder timeframe before saving.');
      setTimeout(() => setErrorMessage && setErrorMessage(''), 2000);
      return;
    }

    try {
      const db = getFirestore();
      const q = query(collection(db, "users"), where("uid", "==", pageOwnerId));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docSnapshot = querySnapshot.docs[0];
        const userDocRef = doc(db, "users", docSnapshot.id);
        const userData = docSnapshot.data();
        const existingCards = userData.pages?.eventsPageConfig?.cards || [];

        // Resolve eventId from the widget itself
        let resolvedEventId = eventId || null;

        if (!resolvedEventId && widgetId) {
          const storedWidget = existingCards.find(c => c.id === widgetId);
          if (storedWidget) {
            resolvedEventId = storedWidget.eventId || null;
            if (resolvedEventId) setEventId(resolvedEventId);
          }
        }

        if (!resolvedEventId && targetWidgetId) {
          const tWidget = existingCards.find(c => c.id === targetWidgetId);
          if (tWidget) {
            resolvedEventId = tWidget.eventId || null;
            if (resolvedEventId) setEventId(resolvedEventId);
          }
        }

        // If still no resolved ID, create a new event id
        if (!resolvedEventId) {
          resolvedEventId = `event_${Date.now()}`;
          setEventId(resolvedEventId);
        }

        // Find the target widget card to update — prefer ID-based lookup
        let targetIndex = null;
        if (targetWidgetId) {
          targetIndex = existingCards.findIndex(c => c.id === targetWidgetId);
        }
        if ((targetIndex === -1 || targetIndex === null) && widgetId) {
          targetIndex = existingCards.findIndex(c => c.id === widgetId);
        }
        if ((targetIndex === -1 || targetIndex === null) && typeof targetWidgetIndex === 'number' && targetWidgetIndex >= 0 && targetWidgetIndex < existingCards.length) {
          targetIndex = targetWidgetIndex;
        }
        if ((targetIndex === -1 || targetIndex === null) && typeof widgetIndex === 'number' && widgetIndex >= 0 && widgetIndex < existingCards.length) {
          targetIndex = widgetIndex;
        }

        let beforeSnapshot = null; // captured before any mutation for audit log
        if (typeof targetIndex === 'number' && targetIndex >= 0 && targetIndex < existingCards.length) {
          // Build update object from existing card, filtering out undefined values
          const cleanedExisting = {};
          const existing = existingCards[targetIndex];
          Object.keys(existing).forEach(key => {
            if (existing[key] !== undefined) {
              cleanedExisting[key] = existing[key];
            }
          });
          
          // Capture before-snapshot for audit BEFORE any mutation
          beforeSnapshot = { ...existing };

          // Build candidate update object
          const candidateData = {
            ...cleanedExisting,
            type: 'Event',
            eventId: resolvedEventId || null,
            eventTitle: effectiveEventDetails.title || '',
            eventDate: effectiveEventDetails.date || '',
            eventEndDate: effectiveEventDetails.endDate || '',
            eventLocation: effectiveEventDetails.location || '',
            eventLocationCoords: effectiveEventDetails.locationCoords || null,
            locationCoords: effectiveEventDetails.locationCoords || null,
            eventDescription: effectiveEventDetails.description || '',
            eventUrl: effectiveEventUrl,
            rsvpEnabled: !!effectiveRsvpEnabled,
            reminderSettings: effectiveReminderSettings
          };

          // WRITE GUARD: block empty data from overwriting valid existing data
          if (wouldOverwriteValidCard(existing, candidateData)) {
            const validation = validateEventCard(candidateData);
            logWidgetEvent(docSnapshot.id, widgetId || existing.id || 'unknown', 'Event', 'widget_validation_failed', {
              source: 'UI', caller: 'saveEventDetails',
              errors: validation.errors,
              before: existing,
            });
            console.warn('[saveEventDetails] BLOCKED — incoming data would overwrite valid card with empty fields:', validation.errors);
            setErrorMessage && setErrorMessage('Cannot save: event data appears incomplete. Please fill in the event title.');
            setTimeout(() => setErrorMessage && setErrorMessage(''), 3000);
            return;
          }

          // Safe merge: prefer non-empty incoming fields, never blank-out existing non-empty fields
          existingCards[targetIndex] = safeEventCardMerge(existing, candidateData);
        } else {
          // No matching widget found — create a new card and append it
          const newCardId = widgetId || `widget_${Date.now()}`;
          const newCard = {
            id: newCardId,
            type: 'Event',
            content: '',
            position: existingCards.length,
            metadata: {},
            events: [],
            eventPhotos: [],
            eventId: resolvedEventId || null,
            eventTitle: effectiveEventDetails.title || '',
            eventDate: effectiveEventDetails.date || '',
            eventEndDate: effectiveEventDetails.endDate || '',
            eventLocation: effectiveEventDetails.location || '',
            eventLocationCoords: effectiveEventDetails.locationCoords || null,
            locationCoords: effectiveEventDetails.locationCoords || null,
            eventDescription: effectiveEventDetails.description || '',
            eventUrl: effectiveEventUrl,
            rsvpEnabled: !!effectiveRsvpEnabled,
            reminderSettings: effectiveReminderSettings,
            going: [],
            maybe: [],
            decline: []
          };
          existingCards.push(newCard);
          targetIndex = existingCards.length - 1;
        }

        // Clear the needsReview flag now that valid content (title + date) is present.
        if (typeof targetIndex === 'number' && targetIndex >= 0 && targetIndex < existingCards.length) {
          const savedCard = existingCards[targetIndex];
          if (savedCard && (savedCard.needsReview || savedCard.status === 'incomplete')) {
            existingCards[targetIndex] = { ...savedCard, needsReview: false, status: 'active' };
          }
        }

        // AUTO-NORMALIZE LOW-severity structural issues (missing arrays, wrong types).
        // This runs silently — no modal, no audit event, no user-visible change.
        // LOW issues are cosmetic/structural only (e.g. undefined arrays) and safe to fix
        // at write time without admin intervention or Fix flows.
        const normalizedCards = existingCards.map(c => {
          const vCheck = validateWidget(c);
          if (vCheck.severity === 'low') {
            const { repaired } = repairWidget(c);
            return repaired;
          }
          return c;
        });

        // Deep-clean ALL cards to remove any undefined values anywhere in the array
        const cleanedCards = cleanForFirestore(normalizedCards);
        const isNewWidget = targetIndex === normalizedCards.length - 1 && !normalizedCards[targetIndex]?.eventTitle;

        if (isPreviewMode) {
          if (typeof window !== 'undefined' && window.updateParentCards) {
            try {
              const notifyIndex = (typeof targetIndex === 'number' && targetIndex >= 0 && targetIndex < cleanedCards.length) ? targetIndex : null;
              if (typeof notifyIndex === 'number') {
                window.updateParentCards(notifyIndex, cleanedCards[notifyIndex]);
              }
            } catch (e) {
              console.warn('saveEventDetails: failed to notify parent during preview:', e);
            }
          }
          setHasChanges && setHasChanges(false);
          setSuccessMessage && setSuccessMessage('Event details updated in preview. Changes are not saved.');
          setTimeout(() => setSuccessMessage && setSuccessMessage(''), 2500);
          return;
        }

        await updateDoc(userDocRef, { 'pages.eventsPageConfig.cards': cleanedCards });

        // Audit log — fire-and-forget, never awaited in the critical path
        logWidgetEvent(
          docSnapshot.id,
          widgetId || cleanedCards[targetIndex]?.id || 'unknown',
          'Event',
          isNewWidget ? 'widget_created' : 'widget_updated',
          {
            source: 'user',
            caller: 'saveEventDetails',
            before: isNewWidget ? null : (beforeSnapshot || null),
            after: cleanedCards[targetIndex] || null,
          }
        );

        // Mirror to email-keyed doc if needed.
        // SAFETY: use updateDoc with dot-notation rather than setDoc({pages:{eventsPageConfig:{...}}},
        // {merge:true}) which would wipe eventsCustomBackground / eventsTextColor on the email doc.
        try {
          const emailId = userData.email?.toLowerCase();
          if (emailId && emailId !== docSnapshot.id) {
            try {
              await updateDoc(doc(db, 'users', emailId), { 'pages.eventsPageConfig.cards': cleanedCards });
            } catch (mirrorErr) {
              // Email-keyed doc may not exist yet — fall back to setDoc with explicit spread
              // so we still only write the cards field at the correct nested path.
              try {
                await setDoc(
                  doc(db, 'users', emailId),
                  { pages: { eventsPageConfig: { cards: cleanedCards } } },
                  { merge: true }
                );
              } catch (err) {
                console.warn('saveEventDetails: failed to mirror to email doc:', err);
              }
            }
          }
        } catch (err) {
          // ignore
        }

        // Notify parent of updated widget
        if (typeof window !== 'undefined' && window.updateParentCards) {
          try {
            const notifyIndex = (typeof targetIndex === 'number' && targetIndex >= 0 && targetIndex < cleanedCards.length) ? targetIndex : null;
            if (typeof notifyIndex === 'number') {
              try { window.updateParentCards(notifyIndex, cleanedCards[notifyIndex]); } catch (e) { console.warn('Failed to notify parent:', e); }
            }
          } catch (e) {
            console.warn('Failed to run updateParentCards:', e);
          }
        }

        setHasChanges && setHasChanges(false);
        setSuccessMessage && setSuccessMessage('Event details saved successfully!');
        setTimeout(() => setSuccessMessage && setSuccessMessage(''), 2000);

        // Reschedule reminders if RSVP is enabled
        if (effectiveRsvpEnabled) {
          try {
            const { SERVER_URL } = await import('../config.js');
            await fetch(`${SERVER_URL}/reschedule-event-reminders`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                eventOwnerId: pageOwner.email.toLowerCase(),
                updatedEventData: {
                  title: effectiveEventDetails.title,
                  date: effectiveEventDetails.date,
                  location: effectiveEventDetails.location,
                  description: effectiveEventDetails.description,
                  eventId: resolvedEventId
                },
                reminderSettings: effectiveReminderSettings
              })
            });
          } catch (err) {
            console.error('Failed to update reminders:', err);
          }
        }
      }
    } catch (error) {
      console.error('Failed to save event details:', error);
      setErrorMessage && setErrorMessage('Failed to save event details. Please try again.');
      setTimeout(() => setErrorMessage && setErrorMessage(''), 2000);
    }
  };

  return {
    modalMapContainerRef,
    modalMapRef,
    modalMarkerRef,
    modalGeocoderRef,
    modalMapInitialized,
    viewMapContainerRef,
    viewMapRef,
    viewMarkerRef,
    searchLocations,
    handleLocationChange,
    selectLocation,
    locationSuggestions,
    showLocationSuggestions,
    setShowLocationSuggestions,
    lastSelectedLocationRef,
    saveEventDetails,
    localTitle,
    localDescription,
    setLocalTitle,
    setLocalDescription,
    debouncedSetTitle,
    debouncedSetDescription,
    cleanForFirestore,
  };
};

// Shared editor styles
const editorColors = {
  primary: '#4c956c',
  primaryDark: '#2d5a3f',
  cream: '#fefde2',
  border: 'rgba(76, 149, 108, 0.2)',
  borderFocus: 'rgba(76, 149, 108, 0.5)',
  inputBg: '#fff',
  labelColor: '#2d5a3f',
  sublabelColor: '#777',
};

const editorInputStyle = {
  borderWidth: 1,
  borderColor: editorColors.border,
  borderRadius: 12,
  padding: 14,
  fontSize: 15,
  backgroundColor: editorColors.inputBg,
  fontFamily: 'Orpheus Pro, serif',
  color: '#333',
  outline: 'none',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
};

const editorInputFocusStyle = {
  borderColor: editorColors.borderFocus,
  boxShadow: '0 0 0 3px rgba(76, 149, 108, 0.1)',
};

// Multi-step Event editor wizard. Each step focuses on one aspect of the event for a fun, guided experience.
// Steps: 1) Title  2) Date & Time  3) Location  4) Details  5) Photos  6) Link/URL  7) RSVP & Reminders
const STEP_COUNT = 7;
const stepMeta = [
  { num: 1, label: "What's the occasion?", sub: 'Give your event a name' },
  { num: 2, label: 'When is it happening?', sub: 'Pick a date (required) — time is optional' },
  { num: 3, label: 'Tell them more', sub: 'Add a description for your guests (optional)' },
  { num: 4, label: 'Where is it?', sub: 'Search or pin your location (optional)' },
  { num: 5, label: 'Add some photos', sub: 'Upload photos for your event (optional)' },
  { num: 6, label: 'Got a link?', sub: 'Add a website, ticket link, or anything else (optional)' },
  { num: 7, label: 'Final touches', sub: 'Enable RSVP and reminders (optional)' },
];

// Rotating fun placeholder suggestions for event title
const TITLE_PLACEHOLDERS = [
  'The Big Walk',
  'Cap & Gown Party',
  'Grad Party',
  'Come for the Cake',
  'Cheers to the Grad',
  'Commencement',
  'Open House',
  'Post Graduation Celebration',
  'Diploma & Drinks',
  'Tassel Was Worth the Hassle',
];

export const EventsEditor = ({
  eventDetails,
  setEventDetails,
  isEditingEvent,
  setIsEditingEvent,
  handleLocationChange,
  locationSuggestions,
  showLocationSuggestions,
  setShowLocationSuggestions,
  selectLocation,
  modalMapContainerRef,
  saveEventDetails,
  formatDateTimeForInput,
  // RSVP toggle props
  rsvpEnabled,
  setRsvpEnabled,
  // Reminder settings
  reminderSettings,
  setReminderSettings,
  // Photo step props (optional — passed from EventCard)
  eventPhotos,
  onPhotosChange,
  uploadingPhotos,
  setUploadingPhotos,
  pageOwnerId,
  pageOwner,
  // URL step
  eventUrl,
  onUrlChange,
  // Feature flags
  featureFlags,
  myTasselPlus,
  eventImagesFeature,
  // Local state for inputs
  localTitle,
  localDescription,
  setLocalTitle,
  setLocalDescription,
  debouncedSetTitle,
  debouncedSetDescription,
  lastSelectedLocationRef,
  // optional error message setter (passed from EventCard)
  setErrorMessage,
  isPreviewMode = false,
}) => {
  const locationInputRef = React.useRef(null);
  const portalContainerRef = React.useRef(null);
  const recentSelectionRef = React.useRef(false);
  const recentSelectionTimeoutRef = React.useRef(null);
  const [saving, setSaving] = React.useState(false);
  const canUseEventImages = myTasselPlus?.canUseFeature?.(eventImagesFeature) === true;
  const requireEventImages = () => {
    if (!myTasselPlus || !eventImagesFeature) return true;
    return myTasselPlus.requireFeature(eventImagesFeature);
  };
  const [step, setStep] = React.useState(1);

  // Dynamic reminder options fetched from Firestore (falls back to defaults)
  const [dynamicReminderOptions, setDynamicReminderOptions] = React.useState([
    { value: 'days_before_1', label: 'Day of + 1 day before' },
    { value: 'days_before_3', label: 'Day of + 3 days before' },
    { value: 'days_before_7', label: 'Day of + 1 week before' },
    { value: 'days_before_10', label: 'Day of + 10 days before' },
  ]);
  React.useEffect(() => {
    const db = getFirestore();
    getDoc(doc(db, 'config', 'reminderSettings')).then(snap => {
      if (snap.exists() && snap.data().defaultOptions?.length) {
        setDynamicReminderOptions(snap.data().defaultOptions.map(opt => ({ value: opt.value, label: opt.label })));
      }
    }).catch(() => {});
  }, []);
  
  // Track whether title input is focused so we can pause the rotating placeholder
  const [titleFocused, setTitleFocused] = React.useState(false);
  // Track whether description textarea is focused to avoid stomping while editing
  const [descFocused, setDescFocused] = React.useState(false);
  // Local editor-controlled inputs to avoid parent re-renders clearing typing
  const [titleState, setTitleState] = React.useState(localTitle || '');
  const [descState, setDescState] = React.useState(localDescription || '');

  // Initialize local inputs when the editor opens or when incoming props change
  React.useEffect(() => {
    if (isEditingEvent) {
      console.log('[EventsEditor] initialize local inputs ->', { localTitle, localDescription });
      setTitleState(localTitle || '');
      setDescState(localDescription || '');
    }
  }, [isEditingEvent]);

  // Keep local inputs in sync with external changes, but avoid stomping while focused
  React.useEffect(() => {
    if (!titleFocused) {
      console.log('[EventsEditor] syncing titleState from localTitle ->', localTitle, ' titleFocused=', titleFocused);
      setTitleState(localTitle || '');
    }
  }, [localTitle, titleFocused]);

  React.useEffect(() => {
    if (!descFocused) setDescState(localDescription || '');
  }, [localDescription, descFocused]);

  // Local location state so typing/selecting doesn't get stomped by parent renders
  const [locationState, setLocationState] = React.useState(eventDetails.location || '');
  const [locationFocused, setLocationFocused] = React.useState(false);
  const [locationSelected, setLocationSelected] = React.useState(false);

  // Listen for programmatic selection events (emitted by the hook) so the editor
  // can update its local input state immediately and avoid being stomped.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e) => {
      try {
        const loc = e?.detail?.location || '';
        const coords = e?.detail?.coords || null;
        if (loc) {
          console.log('[EventsEditor] received selectedLocation ->', loc, coords);
          console.log('[EventsEditor] received selectedLocation -> BEFORE setLocationState: locationState=', locationState, ' locationSelected=', locationSelected, ' lastSelected=', lastSelectedLocationRef && lastSelectedLocationRef.current);

          // mark a recent programmatic selection so blur/write timers won't stomp it
          recentSelectionRef.current = true;
          if (recentSelectionTimeoutRef.current) clearTimeout(recentSelectionTimeoutRef.current);
          recentSelectionTimeoutRef.current = setTimeout(() => { recentSelectionRef.current = false; recentSelectionTimeoutRef.current = null; }, 800);

          setLocationState(loc);
          setLocationSelected(true);
          try { console.log('[EventsEditor] received selectedLocation -> calling setEventDetails(location,coords)'); setEventDetails && setEventDetails(prev => ({ ...prev, location: loc, locationCoords: coords || (prev && prev.locationCoords) })); } catch (err) {}
          // keep the sentinel so the existing sync effect knows this was an explicit selection
          if (lastSelectedLocationRef) lastSelectedLocationRef.current = loc;
          console.log('[EventsEditor] received selectedLocation -> AFTER setEventDetails, sentinel=', lastSelectedLocationRef.current);
        }
      } catch (err) { /* ignore */ }
    };
    window.addEventListener('eventsWidget:selectedLocation', handler);
    return () => window.removeEventListener('eventsWidget:selectedLocation', handler);
  }, [setEventDetails]);

  // cleanup recentSelection timeout on unmount
  React.useEffect(() => {
    return () => {
      if (recentSelectionTimeoutRef.current) {
        clearTimeout(recentSelectionTimeoutRef.current);
        recentSelectionTimeoutRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    console.log('[EventsEditor] sync effect ->', { eventDetailsLocation: eventDetails.location, locationFocused, locationSelected, locationState, lastSelected: lastSelectedLocationRef && lastSelectedLocationRef.current });
    if (!locationFocused) {
      // If we just explicitly selected a location, prefer the editor's selected value
      if (lastSelectedLocationRef.current) {
        // Parent has acknowledged the selection — sync local state and clear the sentinel
        if (eventDetails.location === lastSelectedLocationRef.current) {
          console.log('[EventsEditor] sync effect: parent matches sentinel -> applying and clearing sentinel');
          setLocationState(eventDetails.location || '');
          setLocationSelected(false);
          lastSelectedLocationRef.current = null;
          return;
        }

        // Parent hasn't reflected the selected location yet — don't overwrite the
        // editor's local selection while the sentinel is present (prevents races).
        if (locationSelected) {
          console.log('[EventsEditor] sync effect: locationSelected true, skipping overwrite');
          return;
        }

        // If parent unexpectedly cleared the value, keep local selection until user acts
        if (!eventDetails.location) {
          console.log('[EventsEditor] sync effect: parent cleared value, keeping local selection');
          return;
        }
      }

      // No active selection sentinel — safe to sync from parent to local input.
      if (eventDetails.location !== locationState) {
        console.log('[EventsEditor] sync effect: syncing from parent ->', eventDetails.location);
        setLocationState(eventDetails.location || '');
      }
      setLocationSelected(false);
    }
  }, [eventDetails.location, locationFocused, locationSelected, locationState]);

  // Local date/time state will be initialized later (after helper funcs)

  // Checkbox state for optional date/time
  const [includeDate, setIncludeDate] = React.useState(() => {
    return !!(eventDetails.date);
  });
  const [includeStartTime, setIncludeStartTime] = React.useState(() => {
    // Check if the existing date already has a time component
    const str = String(eventDetails.date || '');
    return /T\d{2}:\d{2}/.test(str);
  });
  const [includeEndTime, setIncludeEndTime] = React.useState(() => {
    const str = String(eventDetails.endDate || '');
    return !!(str && /T\d{2}:\d{2}/.test(str));
  });

  // Rotating placeholder for event title
  const [titlePlaceholderIdx, setTitlePlaceholderIdx] = React.useState(0);
  React.useEffect(() => {
    const interval = setInterval(() => {
      // Pause rotating placeholder while the user is focused in the title input
      if (!titleFocused) setTitlePlaceholderIdx(prev => (prev + 1) % TITLE_PLACEHOLDERS.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [titleFocused]);

  // Safely extract date/time parts without timezone drift
  const extractPart = (field, regex) => {
    const str = String(eventDetails[field] || '');
    if (!str) return '';
    const m = str.match(regex);
    if (m) return m[1];
    try {
      const d = new Date(str);
      if (isNaN(d.getTime())) return '';
      if (regex.source.includes('\\d{4}')) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } catch { return ''; }
  };

  const getDatePart = () => extractPart('date', /^(\d{4}-\d{2}-\d{2})/);
  const getTimePart = () => extractPart('date', /T(\d{2}:\d{2})/);
  const getEndTimePart = () => {
    if (!eventDetails.endDate) return '';
    const str = String(eventDetails.endDate);
    const tMatch = str.match(/T(\d{2}:\d{2})/);
    if (tMatch) return tMatch[1];
    // If it's just a time string already
    if (/^\d{2}:\d{2}/.test(str)) return str.substring(0, 5);
    try {
      const d = new Date(str);
      if (isNaN(d.getTime())) return '';
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } catch { return ''; }
  };

  // Local date/time state to prevent parent re-renders from resetting inputs
  const defaultDateStr = `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-${String(new Date().getDate()).padStart(2,'0')}`;
  const [dateState, setDateState] = React.useState(() => getDatePart() || '');
  const [startTimeState, setStartTimeState] = React.useState(() => getTimePart() || '');
  const [endTimeState, setEndTimeState] = React.useState(() => getEndTimePart() || '');

  React.useEffect(() => {
    if (isEditingEvent) {
      setDateState(getDatePart() || '');
      setStartTimeState(getTimePart() || '');
      setEndTimeState(getEndTimePart() || '');
    }
  }, [isEditingEvent]);

  // Portal for location suggestions (web only)
  React.useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!portalContainerRef.current) {
      const el = document.createElement('div');
      el.style.position = 'absolute';
      el.style.zIndex = '2147483647';
      el.style.pointerEvents = 'auto';
      document.body.appendChild(el);
      portalContainerRef.current = el;
    }

    const handleResize = () => {
      try {
        if (!portalContainerRef.current || !locationInputRef.current) return;
        const rect = locationInputRef.current.getBoundingClientRect();
        portalContainerRef.current.style.left = `${rect.left + window.scrollX}px`;
        portalContainerRef.current.style.top = `${rect.bottom + window.scrollY}px`;
        portalContainerRef.current.style.width = `${rect.width}px`;
      } catch (e) {}
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
      if (portalContainerRef.current && portalContainerRef.current.parentNode) {
        portalContainerRef.current.parentNode.removeChild(portalContainerRef.current);
        portalContainerRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (typeof document === 'undefined' || !showLocationSuggestions) return;
    const onDocClick = (e) => {
      try {
        if (portalContainerRef.current && portalContainerRef.current.contains(e.target)) return;
        if (locationInputRef.current && (locationInputRef.current.contains ? locationInputRef.current.contains(e.target) : locationInputRef.current === e.target)) return;
        setShowLocationSuggestions(false);
      } catch (err) {}
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showLocationSuggestions, setShowLocationSuggestions]);

  React.useEffect(() => {
    if (typeof document === 'undefined' || !showLocationSuggestions) return;
    try {
      if (!portalContainerRef.current || !locationInputRef.current) return;
      const rect = locationInputRef.current.getBoundingClientRect();
      portalContainerRef.current.style.left = `${rect.left + window.scrollX}px`;
      portalContainerRef.current.style.top = `${rect.bottom + window.scrollY}px`;
      portalContainerRef.current.style.width = `${rect.width}px`;
    } catch (e) {}
  }, [showLocationSuggestions, locationSuggestions]);

  // Local URL state to avoid parent overwrites while editing
  const [urlState, setUrlState] = React.useState(eventUrl || eventDetails.url || '');
  const [urlFocused, setUrlFocused] = React.useState(false);
  React.useEffect(() => {
    if (!urlFocused) setUrlState(eventUrl || eventDetails.url || '');
  }, [eventUrl, eventDetails.url, urlFocused]);

  // Local RSVP and reminder state to prevent parent re-renders from resetting toggles
  const [rsvpEnabledState, setRsvpEnabledState] = React.useState(false);
  const [reminderSettingsState, setReminderSettingsState] = React.useState({ enabled: false, reminderTimes: [] });

  // flag indicating invalid reminder configuration (enabled but no times chosen)
  const reminderInvalid = reminderSettingsState.enabled &&
    (!reminderSettingsState.reminderTimes || reminderSettingsState.reminderTimes.length === 0);


  React.useEffect(() => {
    // Initialize local RSVP/reminder state only when editing starts.
    // Do NOT re-sync on subsequent prop changes while the editor is open
    if (isEditingEvent) {
      setRsvpEnabledState(!!rsvpEnabled);
      setReminderSettingsState(reminderSettings || { enabled: false, reminderTimes: [] });
    }
  }, [isEditingEvent]);

  // Handle RSVP toggle
  const handleRsvpToggle = React.useCallback(() => {
    const newValue = !rsvpEnabledState;
    console.log('[EventsEditor] handleRsvpToggle ->', newValue);
    setRsvpEnabledState(newValue);
    if (setRsvpEnabled) {
      setRsvpEnabled(newValue);
    }
  }, [rsvpEnabledState, setRsvpEnabled]);

  // Handle reminder settings toggle
  const handleReminderToggle = React.useCallback(() => {
    const newSettings = {
      ...reminderSettingsState,
      enabled: !reminderSettingsState.enabled
    };
    setReminderSettingsState(newSettings);
    if (setReminderSettings) {
      setReminderSettings(newSettings);
    }
  }, [reminderSettingsState, setReminderSettings]);

  // Debounced URL change handler
  const debouncedSetUrl = React.useCallback(
    debounce((url) => {
      if (setEventDetails) setEventDetails(prev => ({ ...prev, url }));
      if (onUrlChange) onUrlChange(url);
    }, 300),
    [setEventDetails, onUrlChange]
  );

  // Toggle switch component
  const ToggleSwitch = ({ enabled, onToggle, label }) => (
    <Pressable
      onPress={onToggle}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: 'rgba(245, 240, 225, 0.6)',
        borderRadius: 12,
        padding: 14,
        borderWidth: 1,
        borderColor: enabled ? editorColors.primary : editorColors.border,
      }}
    >
      <Text style={{ fontSize: 15, fontWeight: '600', color: editorColors.labelColor, fontFamily: 'Orpheus Pro, serif' }}>{label}</Text>
      <View style={{
        width: 48, height: 28, borderRadius: 14,
        backgroundColor: enabled ? editorColors.primary : '#ccc',
        justifyContent: 'center', padding: 2,
      }}>
        <View style={{
          width: 24, height: 24, borderRadius: 12, backgroundColor: '#fff',
          alignSelf: enabled ? 'flex-end' : 'flex-start',
          shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.2, shadowRadius: 2, elevation: 2,
        }} />
      </View>
    </Pressable>
  );

  // Track map container width to enforce a square (height = width)
  const [mapSquareSize, setMapSquareSize] = React.useState(null);
  const mapWrapperRef = React.useRef(null);

  // Use ResizeObserver to keep the map square whenever the wrapper width changes
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return;
    const el = mapWrapperRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 50) { // only update when actually visible (not offscreen 0-width)
          setMapSquareSize(w);
        }
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // When step 4 (location) becomes active, trigger Mapbox resize after layout
  React.useEffect(() => {
    if (step === 4 && typeof window !== 'undefined') {
      const t1 = setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
      const t2 = setTimeout(() => window.dispatchEvent(new Event('resize')), 400);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [step]);

  const canGoNext = () => {
    if (step === 1) {
      const titleVal = (typeof titleState !== 'undefined' ? titleState : (localTitle || eventDetails.title));
      return !!(titleVal && titleVal.trim());
    }
    return true; // all other steps are optional
  };

  const goNext = () => {
    if (!(step < STEP_COUNT && canGoNext())) return;
    // If leaving the title step, immediately persist the title so returning keeps it
    if (step === 1) {
      try {
        console.log('[EventsEditor] goNext persisting title ->', titleState);
        setEventDetails(prev => ({ ...prev, title: titleState }));
      } catch (e) {
        // ignore
      }
    }
    // Persist description immediately when leaving the description step
    if (step === 3) {
      try {
        setEventDetails(prev => ({ ...prev, description: descState }));
      } catch (e) {
        // ignore
      }
    }
    setStep(step + 1);
  };
  const goBack = () => { if (step > 1) setStep(step - 1); };

  const currentMeta = stepMeta[step - 1];

  // Progress dots
  const ProgressDots = () => (
    <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
      {stepMeta.map((_, idx) => (
        <Pressable key={idx} onPress={() => { if (idx + 1 <= step || canGoNext()) setStep(idx + 1); }}>
          <View style={{
            width: idx + 1 === step ? 24 : 10,
            height: 10,
            borderRadius: 5,
            backgroundColor: idx + 1 === step ? editorColors.primary : (idx + 1 < step ? 'rgba(76, 149, 108, 0.4)' : 'rgba(76, 149, 108, 0.15)'),
            transition: 'all 0.3s ease',
          }} />
        </Pressable>
      ))}
    </View>
  );

  return (
    <View style={{ padding: 20, alignSelf: 'stretch' }}>
      {/* Header */}
      <View style={{ alignItems: 'center', marginBottom: 8 }}>
        <Text style={{ color: editorColors.primaryDark, fontSize: 24, fontWeight: 'bold', fontFamily: 'Bodoni72Smallcaps', textAlign: 'center' }}>Plan your event</Text>
        <Text style={{ color: editorColors.sublabelColor, fontSize: 13, fontFamily: 'Orpheus Pro, serif', marginTop: 2 }}>Step {step} of {STEP_COUNT}</Text>
      </View>

      <ProgressDots />

      {/* Step title */}
      <View style={{ alignItems: 'center', marginBottom: 20 }}>
        <Text style={{ fontSize: 20, fontWeight: '700', color: editorColors.primaryDark, fontFamily: 'Bodoni72Smallcaps', textAlign: 'center' }}>{currentMeta.label}</Text>
        <Text style={{ fontSize: 13, color: editorColors.sublabelColor, fontFamily: 'Orpheus Pro, serif', marginTop: 4, textAlign: 'center' }}>{currentMeta.sub}</Text>
      </View>

      {/* ===== STEP 1: Title ===== */}
      {step === 1 && (
        <View>
          <Text style={{ fontSize: 13, fontWeight: '600', color: editorColors.labelColor, marginBottom: 6, fontFamily: 'Orpheus Pro, serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>Event Title *</Text>
          <input
            type="text"
            value={titleState || ''}
            onChange={(e) => {
              console.log('[EventsEditor] title onChange ->', e.target.value);
              setTitleState(e.target.value);
              debouncedSetTitle && debouncedSetTitle(e.target.value);
              try { setEventDetails(prev => ({ ...prev, title: e.target.value })); } catch (_) {}
              try { setLocalTitle && setLocalTitle(e.target.value); } catch (_) {}
            }}
            placeholder={`e.g. ${TITLE_PLACEHOLDERS[titlePlaceholderIdx]}`}
            style={{ ...editorInputStyle, marginBottom: 8, fontSize: 18, textAlign: 'center', padding: 18 }}
            onFocus={(e) => { setTitleFocused(true); Object.assign(e.target.style, editorInputFocusStyle); }}
            onBlur={(e) => {
              setTitleFocused(false);
              e.target.style.borderColor = editorColors.border; e.target.style.boxShadow = 'none';
              console.log('[EventsEditor] title onBlur ->', titleState);
              try { setEventDetails(prev => ({ ...prev, title: titleState })); } catch(_) {}
              try { setLocalTitle && setLocalTitle(titleState); } catch(_) {}
            }}
            autoFocus
          />
          <Text style={{ fontSize: 12, color: '#999', textAlign: 'center', fontFamily: 'Orpheus Pro, serif' }}>This is the only required field — have fun with it!</Text>
        </View>
      )}

      {/* ===== STEP 2: Date & Time ===== */}
      {step === 2 && (
        <View>
          {/* Include Date checkbox */}
          <Pressable
            onPress={() => {
              const next = !includeDate;
              setIncludeDate(next);
              if (!next) {
                // Unchecked — clear date and times locally and in parent
                setDateState('');
                setStartTimeState('');
                setEndTimeState('');
                setIncludeStartTime(false);
                setIncludeEndTime(false);
                setEventDetails(prev => ({ ...prev, date: '', endDate: '' }));
              } else {
                // Checked — set today's date locally and in parent
                const today = new Date();
                const dateStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
                setDateState(dateStr);
                setEventDetails(prev => ({ ...prev, date: dateStr }));
              }
            }}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: includeDate ? 10 : 4, paddingVertical: 6 }}
          >
            <View style={{
              width: 22, height: 22, borderRadius: 6, borderWidth: 2,
              borderColor: includeDate ? editorColors.primary : '#bbb',
              backgroundColor: includeDate ? editorColors.primary : 'transparent',
              alignItems: 'center', justifyContent: 'center',
            }}>
              {includeDate && <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold', lineHeight: 16 }}>✓</Text>}
            </View>
            <Text style={{ fontSize: 14, color: editorColors.labelColor, fontFamily: 'Orpheus Pro, serif' }}>Include a date</Text>
          </Pressable>

          {/* Date input — only shown when checkbox is checked */}
          {includeDate && (
            <View style={{ marginBottom: 14 }}>
              <Text style={{ fontSize: 13, fontWeight: '600', color: editorColors.labelColor, marginBottom: 6, fontFamily: 'Orpheus Pro, serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date</Text>
              <input
                type="date"
                value={dateState}
                onChange={(e) => {
                  const newDate = e.target.value;
                  setDateState(newDate);
                  if (!newDate) {
                    setEventDetails(prev => ({ ...prev, date: '', endDate: '' }));
                    setIncludeStartTime(false);
                    setIncludeEndTime(false);
                    return;
                  }
                  const existingTime = includeStartTime ? (startTimeState || getTimePart()) : '';
                  const dateVal = existingTime ? `${newDate}T${existingTime}` : newDate;
                  setEventDetails(prev => ({ ...prev, date: dateVal }));
                }}
                style={{ ...editorInputStyle, textAlign: 'center', ...(isMobile && step === 2 ? { maxWidth: '90%' } : {}) }}
                onFocus={(e) => Object.assign(e.target.style, editorInputFocusStyle)}
                onBlur={(e) => { e.target.style.borderColor = editorColors.border; e.target.style.boxShadow = 'none'; }}
              />
            </View>
          )}

          {/* Include Start Time checkbox — only shown when date is included */}
          {includeDate && (
            <Pressable
            onPress={() => {
              const next = !includeStartTime;
              setIncludeStartTime(next);
              if (!next) {
                // Unchecked — strip time from date, keep just the date part
                setStartTimeState('');
                const datePart = dateState || getDatePart();
                setEventDetails(prev => ({ ...prev, date: datePart || prev.date }));
                // Also uncheck end time since it doesn't make sense without a start
                setIncludeEndTime(false);
                setEndTimeState('');
                setEventDetails(prev => ({ ...prev, endDate: '' }));
              } else {
                // Checked — set a default time if none exists
                const datePart = dateState || getDatePart() || defaultDateStr;
                const existingTime = startTimeState || getTimePart();
                // Default to 6:00 PM if no time set
                const timeToUse = existingTime || '18:00';
                setStartTimeState(timeToUse);
                setEventDetails(prev => ({ ...prev, date: `${datePart}T${timeToUse}` }));
              }
            }}
            style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: includeStartTime ? 10 : 4, paddingVertical: 6 }}
          >
            <View style={{
              width: 22, height: 22, borderRadius: 6, borderWidth: 2,
              borderColor: includeStartTime ? editorColors.primary : '#bbb',
              backgroundColor: includeStartTime ? editorColors.primary : 'transparent',
              alignItems: 'center', justifyContent: 'center',
            }}>
              {includeStartTime && <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold', lineHeight: 16 }}>✓</Text>}
            </View>
            <Text style={{ fontSize: 14, color: editorColors.labelColor, fontFamily: 'Orpheus Pro, serif' }}>Include a start time</Text>
          </Pressable>
          )}

          {/* Start time input — only shown when checkbox is checked */}
          {includeStartTime && (
            <View style={{ marginBottom: 10 }}>
              <input
                type="time"
                value={startTimeState || ''}
                onChange={(e) => {
                  const timeVal = e.target.value;
                  setStartTimeState(timeVal);
                  const datePart = dateState || getDatePart() || defaultDateStr;
                  if (!timeVal) {
                    setEventDetails(prev => ({ ...prev, date: datePart }));
                  } else {
                    setEventDetails(prev => ({ ...prev, date: `${datePart}T${timeVal}` }));
                  }
                }}
                style={{ ...editorInputStyle, textAlign: 'center', ...(isMobile && step === 2 ? { maxWidth: '90%' } : {}) }}
                onFocus={(e) => Object.assign(e.target.style, editorInputFocusStyle)}
                onBlur={(e) => { e.target.style.borderColor = editorColors.border; e.target.style.boxShadow = 'none'; }}
              />
            </View>
          )}

          {/* Include End Time checkbox — only shown when start time is included */}
          {includeStartTime && (
            <Pressable
              onPress={() => {
                const next = !includeEndTime;
                setIncludeEndTime(next);
                if (!next) {
                  setEndTimeState('');
                  setEventDetails(prev => ({ ...prev, endDate: '' }));
                } else {
                  // Checked — set a default end time (2 hours after start, or 8 PM)
                  const datePart = dateState || getDatePart() || defaultDateStr;
                  const existingEndTime = endTimeState || getEndTimePart();
                  // Default to 8:00 PM if no end time set
                  const timeToUse = existingEndTime || '20:00';
                  setEndTimeState(timeToUse);
                  setEventDetails(prev => ({ ...prev, endDate: `${datePart}T${timeToUse}` }));
                }
              }}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: includeEndTime ? 10 : 4, paddingVertical: 6 }}
            >
              <View style={{
                width: 22, height: 22, borderRadius: 6, borderWidth: 2,
                borderColor: includeEndTime ? editorColors.primary : '#bbb',
                backgroundColor: includeEndTime ? editorColors.primary : 'transparent',
                alignItems: 'center', justifyContent: 'center',
              }}>
                {includeEndTime && <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold', lineHeight: 16 }}>✓</Text>}
              </View>
              <Text style={{ fontSize: 14, color: editorColors.labelColor, fontFamily: 'Orpheus Pro, serif' }}>Include an end time</Text>
            </Pressable>
          )}

          {/* End time input — only shown when both checkboxes are checked */}
          {includeStartTime && includeEndTime && (
            <View style={{ marginBottom: 8 }}>
              <input
                type="time"
                value={endTimeState || ''}
                onChange={(e) => {
                  const timeVal = e.target.value;
                  setEndTimeState(timeVal);
                  if (!timeVal) {
                    setEventDetails(prev => ({ ...prev, endDate: '' }));
                  } else {
                    const datePart = dateState || getDatePart() || defaultDateStr;
                    setEventDetails(prev => ({ ...prev, endDate: `${datePart}T${timeVal}` }));
                  }
                }}
                style={{ ...editorInputStyle, textAlign: 'center', ...(isMobile && step === 2 ? { maxWidth: '90%' } : {}) }}
                onFocus={(e) => Object.assign(e.target.style, editorInputFocusStyle)}
                onBlur={(e) => { e.target.style.borderColor = editorColors.border; e.target.style.boxShadow = 'none'; }}
              />
            </View>
          )}

          <Text style={{ fontSize: 12, color: '#999', fontFamily: 'Orpheus Pro, serif', marginTop: 6, textAlign: 'center' }}>All fields are optional — skip if you're not sure yet</Text>
        </View>
      )}

      {/* ===== STEP 3: Description (moved before Location) ===== */}
      {step === 3 && (
        <View>
          <Text style={{ fontSize: 13, fontWeight: '600', color: editorColors.labelColor, marginBottom: 6, fontFamily: 'Orpheus Pro, serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>Description</Text>
          <textarea
            value={descState || ''}
            onChange={(e) => {
              setDescState(e.target.value);
              debouncedSetDescription && debouncedSetDescription(e.target.value);
              try { setEventDetails(prev => ({ ...prev, description: e.target.value })); } catch (_) {}
              try { setLocalDescription && setLocalDescription(e.target.value); } catch (_) {}
            }}
            placeholder="Tell your guests what to expect..."
            rows={4}
            style={{ ...editorInputStyle, minHeight: 120, resize: 'vertical', marginBottom: 8, fontSize: 16 }}
            onFocus={(e) => { setDescFocused(true); Object.assign(e.target.style, editorInputFocusStyle); }}
            onBlur={(e) => {
              setDescFocused(false);
              e.target.style.borderColor = editorColors.border; e.target.style.boxShadow = 'none';
              try { setEventDetails(prev => ({ ...prev, description: descState })); } catch(_) {}
              try { setLocalDescription && setLocalDescription(descState); } catch(_) {}
            }}
          />
          <Text style={{ fontSize: 12, color: '#999', fontFamily: 'Orpheus Pro, serif' }}>Dress code, parking info, special instructions — whatever helps your guests</Text>
        </View>
      )}

      {/* ===== STEP 4: Location — search input + optional map ===== */}
      {step === 4 && (
        <View>
          <Text style={{ fontSize: 13, fontWeight: '600', color: editorColors.labelColor, marginBottom: 6, fontFamily: 'Orpheus Pro, serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>Location</Text>
          <View style={{ position: 'relative', marginBottom: 12 }}>
            <input
              ref={locationInputRef}
              type="text"
              value={locationState || ''}
              onChange={(e) => { 
                console.log('[EventsEditor] input onChange ->', e.target.value);
                setLocationState(e.target.value); 
                setLocationSelected(false);
                handleLocationChange(e.target.value); 
                setShowLocationSuggestions(true);
                // Immediately sync with parent state
                try { console.log('[EventsEditor] input onChange: setEventDetails(location) ->', e.target.value); setEventDetails(prev => ({ ...prev, location: e.target.value })); } catch(_) {}
              }}
              onFocus={(e) => { 
                setLocationFocused(true); 
                setLocationSelected(false);
                setShowLocationSuggestions(true); 
                Object.assign(e.target.style, editorInputFocusStyle); 
              }}
              onBlur={(e) => { 
                console.log('[EventsEditor] input onBlur -> locationState=', locationState, ' lastSelected=', lastSelectedLocationRef && lastSelectedLocationRef.current, ' locationSelected=', locationSelected);
                setLocationFocused(false); 
                e.target.style.borderColor = editorColors.border; 
                e.target.style.boxShadow = 'none'; 
                setTimeout(() => {
                  // If a recent programmatic selection happened, don't overwrite it with the typed value
                  if (recentSelectionRef.current) {
                    console.log('[EventsEditor] onBlur: recentSelectionRef true -> skipping write');
                    return;
                  }
                  // If an explicit selection sentinel exists, don't overwrite it with the typed value
                  if (lastSelectedLocationRef && lastSelectedLocationRef.current) {
                    console.log('[EventsEditor] onBlur: skipping overwrite because lastSelectedLocationRef=', lastSelectedLocationRef.current);
                    return;
                  }
                  if (!locationSelected) {
                    console.log('[EventsEditor] onBlur: writing typed location ->', locationState);
                    try { setEventDetails(prev => ({ ...prev, location: locationState })); } catch(_) {}
                  }
                }, 200);
              }}
              placeholder={featureFlags?.showEditorMap ? "Search or tap the map to pick a spot..." : "Enter a location..."}
              style={editorInputStyle}
            />

            {/* Inline dropdown fallback */}
            {!portalContainerRef.current && showLocationSuggestions && locationSuggestions.length > 0 && (
              <View style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff', borderRadius: 10, borderWidth: 1, borderColor: editorColors.border, maxHeight: 200, zIndex: 99999, elevation: 99999, overflow: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
                {locationSuggestions.map((suggestion, idx) => {
                      const handleSelect = (e) => {
                        try { e && e.preventDefault && e.preventDefault(); } catch(_){}
                        const selectedLocation = suggestion.display_name;
                        console.log('[EventsEditor] suggestion handleSelect ->', selectedLocation, ' locationState before=', locationState);
                        // mark recent selection so blur/write won't stomp it
                        recentSelectionRef.current = true;
                        if (recentSelectionTimeoutRef.current) clearTimeout(recentSelectionTimeoutRef.current);
                        recentSelectionTimeoutRef.current = setTimeout(() => { recentSelectionRef.current = false; recentSelectionTimeoutRef.current = null; }, 800);
                        setLocationState(selectedLocation);
                        setLocationSelected(true);
                        lastSelectedLocationRef.current = selectedLocation;
                        try { console.log('[EventsEditor] suggestion handleSelect -> calling setEventDetails(location)'); if (setEventDetails) setEventDetails(prev => ({ ...prev, location: selectedLocation })); } catch(_) {}
                        try { if (locationInputRef && locationInputRef.current) { locationInputRef.current.value = selectedLocation; locationInputRef.current.focus(); } } catch(_) {}
                        selectLocation(suggestion);
                        setShowLocationSuggestions(false);
                      };
                      return (
                        <div
                          key={idx}
                          role="button"
                          tabIndex={0}
                          onMouseDown={handleSelect}
                          onTouchStart={handleSelect}
                          onClick={handleSelect}
                          style={{ padding: 14, borderBottomWidth: 1, borderBottomColor: 'rgba(76,149,108,0.08)', backgroundColor: '#fff', cursor: 'pointer' }}
                        >
                          <div style={{ fontSize: 14, color: '#333', fontFamily: 'Orpheus Pro, serif' }}>{suggestion.display_name}</div>
                        </div>
                      );
                    })}
              </View>
            )} 
          </View>
          {featureFlags?.showEditorMap && (
            <Text style={{ fontSize: 12, color: '#999', fontFamily: 'Orpheus Pro, serif', textAlign: 'center', marginBottom: 4 }}>Tap anywhere on the map to drop a pin</Text>
          )}
        </View>
      )}

      {/*
        Map container — gated behind showEditorMap feature flag.
        ALWAYS rendered offscreen so Mapbox can initialize on editing start.
        When on step 4 AND flag is enabled, shown inline with reduced height.
      */}
      <div ref={mapWrapperRef} style={step === 4 && featureFlags?.showEditorMap ? {
        width: '100%',
        height: Math.min(mapSquareSize || 400,400),
        marginBottom: 12,
        borderRadius: 16,
        overflow: 'hidden',
        border: `1px solid ${editorColors.border}`,
      } : {
        position: 'absolute',
        left: -9999,
        top: -9999,
        width: 400,
        height: 400,
        opacity: 0,
        pointerEvents: 'none',
      }}>
        <div ref={modalMapContainerRef} style={{ width: '100%', height: '100%' }} />
      </div>

      {/* ===== STEP 5: Photos ===== */}
      {step === 5 && (
        <View>
          {eventPhotos && eventPhotos.length > 0 && (
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 14, justifyContent: 'center' }}>
              {eventPhotos.map((photo, idx) => (
                <View key={idx} style={{ position: 'relative' }}>
                  <img src={photo} style={{ width: 90, height: 90, borderRadius: 12, objectFit: 'cover', border: `1px solid ${editorColors.border}` }} />
                  <Pressable
                    onPress={() => {
                      if (!requireEventImages()) return;
                      if (onPhotosChange) {
                        const updated = eventPhotos.filter((_, i) => i !== idx);
                        onPhotosChange(updated);
                      }
                    }}
                    style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 11, backgroundColor: '#ef4444', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Text style={{ color: '#fff', fontSize: 13, fontWeight: 'bold', lineHeight: 14 }}>×</Text>
                  </Pressable>
                </View>
              ))}
            </View>
          )}
          <Pressable
            style={({ hovered }) => ({
              borderWidth: 2,
              borderColor: editorColors.border,
              borderStyle: 'dashed',
              borderRadius: 16,
              paddingVertical: 30,
              paddingHorizontal: 20,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: hovered ? 'rgba(76, 149, 108, 0.06)' : 'transparent',
              opacity: uploadingPhotos ? 0.6 : 1,
            })}
            disabled={uploadingPhotos}
            onPress={() => {
              if (uploadingPhotos) return;
              if (!requireEventImages()) return;
              if (typeof document === 'undefined') return;

              const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
              const isIOSWeb = /iPad|iPhone|iPod/.test(ua) || (typeof navigator !== 'undefined' && navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
              console.log('📷 EventsEditor: open photo picker', { isIOSWeb, pageOwnerId: pageOwnerId || null });

              const input = document.createElement('input');
              input.type = 'file';
              input.accept = 'image/*';
              input.multiple = true;
              input.value = '';

              // iOS Safari is picky: the input must be in the DOM when clicked.
              input.style.position = 'fixed';
              input.style.top = '0';
              input.style.left = '0';
              input.style.width = '1px';
              input.style.height = '1px';
              input.style.opacity = '0';
              input.style.pointerEvents = 'none';

              const cleanup = () => {
                try {
                  if (input && input.parentNode) input.parentNode.removeChild(input);
                } catch (e) {
                  // ignore
                }
              };

              const handleFocus = () => {
                // When the picker closes without selection, Safari often returns focus.
                setTimeout(() => {
                  const count = input?.files?.length || 0;
                  if (count === 0) console.log('📷 EventsEditor: picker closed with no selection');
                  cleanup();
                }, 400);
              };

              window.addEventListener('focus', handleFocus, { once: true });

              input.onchange = async (changeEvent) => {
                const files = Array.from(changeEvent.target.files || []);
                console.log('📷 EventsEditor: files selected', files.length);
                cleanup();
                if (files.length === 0) return;

                if (setUploadingPhotos) setUploadingPhotos(true);
                try {
                  if (isPreviewMode) {
                    const previewPhotos = files.map(file => URL.createObjectURL(file));
                    if (onPhotosChange) onPhotosChange([...(eventPhotos || []), ...previewPhotos]);
                    if (setUploadingPhotos) setUploadingPhotos(false);
                    return;
                  }

                  const storage = getStorage();
                  const newPhotos = [...(eventPhotos || [])];
                  for (const file of files) {
                    const safeName = (file && file.name) ? file.name : 'photo.jpg';
                    const fileRef = storageRef(storage, `eventPhotos/${pageOwnerId || 'anon'}/${Date.now()}_${safeName}`);
                    await uploadBytes(fileRef, file);
                    const url = await getDownloadURL(fileRef);
                    newPhotos.push(url);
                  }
                  if (onPhotosChange) onPhotosChange(newPhotos);
                } catch (err) {
                  console.error('Photo upload failed:', err);
                }
                if (setUploadingPhotos) setUploadingPhotos(false);
              };

              document.body.appendChild(input);
              try {
                input.click();
              } catch (err) {
                console.error('📷 EventsEditor: input.click() failed', err);
                cleanup();
              }
            }}
          >
            <Text style={{ fontSize: 15, fontWeight: '600', color: editorColors.primary, fontFamily: 'Bodoni72Smallcaps' }}>
              {uploadingPhotos ? 'Uploading...' : `${eventPhotos && eventPhotos.length > 0 ? 'Add More Photos' : 'Choose Photos'} ${!canUseEventImages ? '+' : ''}`}
            </Text>
            <Text style={{ fontSize: 12, color: '#999', fontFamily: 'Orpheus Pro, serif', marginTop: 4 }}>JPG, PNG — tap to browse</Text>
          </Pressable>
        </View>
      )}

      {/* ===== STEP 6: URL / Link ===== */}
      {step === 6 && (
        <View>
          <Text style={{ fontSize: 13, fontWeight: '600', color: editorColors.labelColor, marginBottom: 6, fontFamily: 'Orpheus Pro, serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>Link / URL</Text>
          <input
            type="url"
            value={urlState || ''}
            onChange={(e) => {
              setUrlState(e.target.value);
              debouncedSetUrl(e.target.value);
            }}
            onFocus={(e) => { setUrlFocused(true); Object.assign(e.target.style, editorInputFocusStyle); }}
            onBlur={(e) => { 
              setUrlFocused(false);
              e.target.style.borderColor = editorColors.border; 
              e.target.style.boxShadow = 'none'; 
              try { setEventDetails(prev => ({ ...prev, url: urlState })); } catch(_) {} 
              if (onUrlChange) onUrlChange(urlState); 
            }}
            placeholder="e.g. https://tickets.example.com"
            style={{ ...editorInputStyle, marginBottom: 8 }}
          />
          <Text style={{ fontSize: 12, color: '#999', fontFamily: 'Orpheus Pro, serif' }}>Ticket page, registry, playlist — anything you want to share</Text>
        </View>
      )}

      {/* ===== STEP 7: RSVP & Reminders ===== */}
      {step === 7 && (
        <View>
          <View style={{ gap: 10, marginBottom: 16 }}>
            <ToggleSwitch
              enabled={!!rsvpEnabledState}
              onToggle={handleRsvpToggle}
              label="Enable RSVP"
            />

            <ToggleSwitch
              enabled={!!(reminderSettingsState && reminderSettingsState.enabled)}
              onToggle={handleReminderToggle}
              label="Enable Reminders"
            />
            {reminderSettingsState.enabled &&
              (!reminderSettingsState.reminderTimes || reminderSettingsState.reminderTimes.length === 0) && (
              <Text style={{ color: '#b45309', fontSize: 12, marginTop: 4 }}>Please select a reminder timeframe before saving.</Text>
            )}
          </View>

          {/* Reminder options */}
          {reminderSettingsState && reminderSettingsState.enabled && (
            <View style={{ backgroundColor: 'rgba(245, 240, 225, 0.4)', borderRadius: 12, padding: 14, marginBottom: 16, borderWidth: 1, borderColor: editorColors.border }}>
              <Text style={{ fontSize: 13, fontWeight: '600', color: editorColors.labelColor, marginBottom: 10, fontFamily: 'Orpheus Pro, serif' }}>Reminder Schedule</Text>
              {dynamicReminderOptions.map(option => (
                <Pressable
                  key={option.value}
                  onPress={() => {
                      console.log('[EventsEditor] reminder option selected ->', option.value);
                      const newTimes = ['day_of', option.value];
                      const newSettings = { ...(reminderSettingsState || {}), reminderTimes: newTimes, enabled: true };
                      setReminderSettingsState(newSettings);
                      if (setReminderSettings) setReminderSettings(newSettings);
                      setRsvpEnabledState(true);
                      if (setRsvpEnabled) setRsvpEnabled(true);
                    }}
                  style={{
                    flexDirection: 'row', alignItems: 'center', gap: 10,
                    paddingVertical: 10, paddingHorizontal: 8, borderRadius: 8,
                    backgroundColor: reminderSettingsState.reminderTimes?.includes(option.value) ? 'rgba(76, 149, 108, 0.1)' : 'transparent',
                    marginBottom: 4,
                  }}
                >
                  <View style={{
                    width: 20, height: 20, borderRadius: 10, borderWidth: 2,
                    borderColor: reminderSettingsState.reminderTimes?.includes(option.value) ? editorColors.primary : '#ccc',
                    backgroundColor: reminderSettingsState.reminderTimes?.includes(option.value) ? editorColors.primary : 'transparent',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    {reminderSettingsState.reminderTimes?.includes(option.value) && (
                      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#fff' }} />
                    )}
                  </View>
                  <Text style={{ fontSize: 14, color: '#444', fontFamily: 'Orpheus Pro, serif' }}>{option.label}</Text>
                </Pressable>
              ))}

              {ADMIN_EMAIL_WHITELIST.includes(pageOwner?.email?.toLowerCase()) && (
                <View style={{ marginTop: 12, borderTopWidth: 1, borderTopColor: '#e0e0e0', paddingTop: 10 }}>
                  <Text style={{ fontSize: 11, fontWeight: '700', color: '#b45309', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Orpheus Pro, serif' }}>Admin Testing</Text>
                  {[
                    { label: 'TEST: Send in 5 minutes', testDelay: 5 },
                    { label: 'TEST: Send in 10 minutes', testDelay: 10 },
                  ].map(opt => (
                    <Pressable
                      key={opt.testDelay}
                      onPress={async () => {
                        try {
                          const { SERVER_URL } = await import('../config.js');
                          const resp = await fetch(`${SERVER_URL}/schedule-event-reminders`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ eventOwnerId: pageOwner.email.toLowerCase(), testMode: true, testDelay: opt.testDelay }),
                          });
                          const result = await resp.json();
                          if (result?.success) {
                            if (setSuccessMessage) setSuccessMessage(`Test reminder scheduled — fires in ${opt.testDelay} min`);
                          } else {
                            if (setErrorMessage) setErrorMessage(result?.reason || result?.error || 'Test failed');
                          }
                        } catch (err) {
                          if (setErrorMessage) setErrorMessage(err.message);
                        }
                      }}
                      style={{ backgroundColor: '#fffbeb', borderRadius: 8, padding: 10, marginBottom: 4 }}
                    >
                      <Text style={{ fontSize: 13, color: '#b45309', fontWeight: '600', fontFamily: 'Orpheus Pro, serif' }}>{opt.label}</Text>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          )}
        </View>
      )}

      {/* Navigation buttons */}
      <View style={{ flexDirection: 'row', gap: 10, marginTop: 24 }}>
        {step === 1 ? (
          <Pressable
            style={{
              flex: 1, backgroundColor: 'transparent', padding: 14, borderRadius: 14,
              alignItems: 'center', borderWidth: 1, borderColor: editorColors.border,
            }}
            onPress={() => setIsEditingEvent(false)}
          >
            <Text style={{ color: '#777', fontWeight: '600', fontSize: 15, fontFamily: 'Bodoni72Smallcaps' }}>Cancel</Text>
          </Pressable>
        ) : (
          <Pressable
            style={{
              flex: 1, backgroundColor: 'transparent', padding: 14, borderRadius: 14,
              alignItems: 'center', borderWidth: 1, borderColor: editorColors.border,
            }}
            onPress={goBack}
          >
            <Text style={{ color: '#777', fontWeight: '600', fontSize: 15, fontFamily: 'Bodoni72Smallcaps' }}>Back</Text>
          </Pressable>
        )}

        {step < STEP_COUNT ? (
          <Pressable
            style={{
              flex: 2, backgroundColor: canGoNext() ? editorColors.primary : '#ccc', padding: 14, borderRadius: 14,
              alignItems: 'center',
              shadowColor: '#1a5c3a', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.25, shadowRadius: 8, elevation: 4,
              opacity: canGoNext() ? 1 : 0.6,
            }}
            onPress={goNext}
            disabled={!canGoNext()}
          >
            <Text style={{ color: '#fff', fontWeight: '700', fontSize: 16, fontFamily: 'Bodoni72Smallcaps' }}>
              {step === 1 ? 'Let\'s go!' : 'Next'}
            </Text>
          </Pressable>
        ) : (
          <Pressable
            style={{
              flex: 2,
              backgroundColor: saving ? '#3d7d5a' : editorColors.primary,
              padding: 14, borderRadius: 14, alignItems: 'center',
              shadowColor: '#1a5c3a', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.25, shadowRadius: 8, elevation: 4,
              opacity: saving || reminderInvalid ? 0.7 : 1,
            }}
            onPress={async () => {
              if (saving || reminderInvalid) {
                if (reminderInvalid && setErrorMessage) {
                  setErrorMessage('Please select a reminder timeframe before saving.');
                  setTimeout(() => setErrorMessage && setErrorMessage(''), 2000);
                }
                return;
              }
              setSaving(true);
              try { 
                // Ensure all local state is properly synced before saving
                    const resolvedLocation = (lastSelectedLocationRef && lastSelectedLocationRef.current) ? lastSelectedLocationRef.current : (locationState || eventDetails.location || '');
                    const updatedEventDetails = {
                      ...eventDetails,
                      title: titleState || eventDetails.title || '',
                      description: descState || eventDetails.description || '',
                      location: resolvedLocation,
                      url: urlState || eventDetails.url || ''
                    };
                
                // Update parent state with final values
                console.log('[EventsEditor] Save Event - updatedEventDetails ->', updatedEventDetails.title, updatedEventDetails.date, updatedEventDetails.location);
                setEventDetails(updatedEventDetails);
                
                // Persist RSVP and reminder settings in parent state for future opens
                if (setRsvpEnabled) setRsvpEnabled(!!rsvpEnabledState);
                if (setReminderSettings) setReminderSettings(reminderSettingsState || { enabled: false, reminderTimes: [] });

                // Save using the most up-to-date values explicitly
                await saveEventDetails({
                  overrideEventDetails: updatedEventDetails,
                  overrideRsvpEnabled: !!rsvpEnabledState,
                  overrideReminderSettings: reminderSettingsState || { enabled: false, reminderTimes: [] },
                  overrideEventUrl: urlState || '',
                });
                
                // Close editor after successful save
                setIsEditingEvent(false);
                setStep(1); // Reset to first step for next time
              } catch (err) { 
                console.error('Save failed:', err);
                // Don't close editor on error so user can try again
              }
              setSaving(false);
            }}
          >
            <Text style={{ color: '#fff', fontWeight: '700', fontSize: 16, fontFamily: 'Bodoni72Smallcaps' }}>{saving ? 'Saving...' : 'Save Event'}</Text>
          </Pressable>
        )}
      </View>

      {/* Skip link for optional steps */}
      {step > 1 && step < STEP_COUNT && (
        <Pressable onPress={goNext} style={{ alignSelf: 'center', marginTop: 12, paddingVertical: 6, paddingHorizontal: 12 }}>
          <Text style={{ color: editorColors.sublabelColor, fontSize: 13, fontFamily: 'Orpheus Pro, serif', textDecorationLine: 'underline' }}>Skip this step</Text>
        </Pressable>
      )}

      {/* Portal rendering for location suggestions on web */}
      {typeof document !== 'undefined' && portalContainerRef.current && showLocationSuggestions && locationSuggestions.length > 0 && ReactDOM.createPortal(
        <div style={{ backgroundColor: '#fff', borderRadius: 10, border: `1px solid ${editorColors.border}`, maxHeight: 300, overflow: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
          {locationSuggestions.map((suggestion, idx) => (
                <div
                  key={idx}
                  style={{ padding: 14, borderBottom: '1px solid rgba(76,149,108,0.08)', cursor: 'pointer', fontFamily: 'Orpheus Pro, serif', fontSize: 14, color: '#333' }}
                  onMouseDown={(e) => {
                    const selectedLocation = suggestion.display_name;
                    // mark recent selection so blur/write won't stomp it
                    recentSelectionRef.current = true;
                    if (recentSelectionTimeoutRef.current) clearTimeout(recentSelectionTimeoutRef.current);
                    recentSelectionTimeoutRef.current = setTimeout(() => { recentSelectionRef.current = false; recentSelectionTimeoutRef.current = null; }, 800);
                    setLocationState(selectedLocation);
                    setLocationSelected(true);
                    lastSelectedLocationRef.current = selectedLocation;
                    try { if (setEventDetails) setEventDetails(prev => ({ ...prev, location: selectedLocation })); } catch(_) {}
                    try { if (locationInputRef && locationInputRef.current) { locationInputRef.current.value = selectedLocation; locationInputRef.current.focus(); } } catch(_) {}
                    selectLocation(suggestion);
                    setShowLocationSuggestions(false);
                  }}
                  onTouchStart={(e) => {
                    const selectedLocation = suggestion.display_name;
                    // mark recent selection so blur/write won't stomp it
                    recentSelectionRef.current = true;
                    if (recentSelectionTimeoutRef.current) clearTimeout(recentSelectionTimeoutRef.current);
                    recentSelectionTimeoutRef.current = setTimeout(() => { recentSelectionRef.current = false; recentSelectionTimeoutRef.current = null; }, 800);
                    setLocationState(selectedLocation);
                    setLocationSelected(true);
                    lastSelectedLocationRef.current = selectedLocation;
                    try { if (setEventDetails) setEventDetails(prev => ({ ...prev, location: selectedLocation })); } catch(_) {}
                    try { if (locationInputRef && locationInputRef.current) { locationInputRef.current.value = selectedLocation; locationInputRef.current.focus(); } } catch(_) {}
                    selectLocation(suggestion);
                    setShowLocationSuggestions(false);
                  }}
                >
                  {suggestion.display_name}
                </div>
              ))}
        </div>,
        portalContainerRef.current
      )}

    </View>
  );
};

export default useEventsWidget;
